/**
 * AdminBackdropsPage ✦ iter230 — /admin/backdrops
 *
 * Full CRUD for the Backdrop Hire catalogue surfaced on
 * /photobooth-hire. Craig can edit prices, descriptions, accent
 * colours, display order, upload cover photos, soft-delete, AND
 * add brand-new backdrops for future additions — all without a
 * redeploy.
 *
 * Mirrors the iter223 inventory + iter222f photobooth-media admin
 * shape so the surface reads familiar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const GOLD_HAIRLINE = 'rgba(212,170,82,0.14)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const pageStyle = {
  minHeight: '100vh', background: '#0F0B1A', color: IVORY,
  fontFamily: SERIF, padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
};

const inputBase = {
  background: 'rgba(0,0,0,0.34)', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontSize: 13.5,
  padding: '8px 10px', borderRadius: 6, width: '100%',
  boxSizing: 'border-box',
};

const ghostPillStyle = {
  background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontSize: 12.5,
  padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
  letterSpacing: '0.02em',
};

const primaryPillStyle = {
  ...ghostPillStyle,
  background: 'rgba(212,170,82,0.14)',
  borderColor: GOLD_FAINT, color: '#FBEED0',
};

const AdminBackdropsPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [backdrops, setBackdrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/backdrops');
  }, [auth?.user, navigate]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/photobooth/backdrops`);
      setBackdrops(r.data.backdrops || []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error('Could not load backdrops.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={pageStyle}>
        <p style={{ fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', marginTop: 80 }}>
          Loading backdrops ✦
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-backdrops-page" style={pageStyle}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <header style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap', marginBottom: 30,
        }}>
          <div>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 8,
            }}>✦ Backdrop Hire</div>
            <h1 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(26px, 4vw, 36px)', margin: 0,
              letterSpacing: '-0.015em',
            }}>Backdrop catalogue</h1>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              color: IVORY_DIM, fontSize: 13.5,
              margin: '8px 0 0', maxWidth: 640, lineHeight: 1.6,
            }}>
              Edit price, copy, photo, ordering and live state for every backdrop. Hidden (inactive) backdrops disappear from <span style={{ color: IVORY }}>/photobooth-hire</span> instantly.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" data-testid="backdrops-new" onClick={() => setCreating(true)}
              style={primaryPillStyle}>+ Add new backdrop</button>
            <button type="button" onClick={() => navigate('/admin')}
              data-testid="admin-back-link" style={ghostPillStyle}>← Admin home</button>
          </div>
        </header>

        {creating && (
          <NewBackdropForm onCancel={() => setCreating(false)}
                           onCreated={async () => { setCreating(false); await load(); }} />
        )}

        <div style={{ display: 'grid', gap: 14 }}>
          {backdrops.map(b => (
            <BackdropRow key={b.slug} backdrop={b} onChanged={load} />
          ))}
          {backdrops.length === 0 && (
            <p style={{ fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', padding: 40 }}>
              No backdrops yet. Click &quot;Add new backdrop&quot; above to start.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const BackdropRow = ({ backdrop, onChanged }) => {
  const [form, setForm] = useState({
    name: backdrop.name || '',
    description: backdrop.description || '',
    price: backdrop.price ?? '',
    accent: backdrop.accent || '',
    display_order: backdrop.display_order ?? 0,
    is_active: backdrop.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const dirty = (
    form.name !== (backdrop.name || '') ||
    form.description !== (backdrop.description || '') ||
    (form.price === '' ? null : Number(form.price)) !== backdrop.price ||
    form.accent !== (backdrop.accent || '') ||
    Number(form.display_order) !== (backdrop.display_order ?? 0) ||
    form.is_active !== (backdrop.is_active !== false)
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/photobooth/backdrops/${backdrop.slug}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        price: form.price === '' || form.price === null ? null : Number(form.price),
        accent: form.accent.trim(),
        display_order: Number(form.display_order) || 0,
        is_active: !!form.is_active,
      });
      toast.success('Saved.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Hide "${backdrop.name}" from the customer page? You can re-enable it later by toggling Active back on.`)) return;
    try {
      await axios.delete(`${API}/admin/photobooth/backdrops/${backdrop.slug}`);
      toast.success('Backdrop hidden.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Max 25MB per photo.');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/photobooth/backdrops/${backdrop.slug}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo uploaded.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearPhoto = async () => {
    if (!window.confirm('Remove this backdrop\'s photo? The card will fall back to a text-led layout.')) return;
    try {
      await axios.delete(`${API}/admin/photobooth/backdrops/${backdrop.slug}/photo`);
      toast.success('Photo removed.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed.');
    }
  };

  return (
    <div data-testid={`backdrop-row-${backdrop.slug}`} style={{
      background: 'rgba(0,0,0,0.28)', border: `1px solid ${GOLD_HAIRLINE}`,
      borderRadius: 10, padding: 18,
      opacity: form.is_active ? 1 : 0.55,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 18, alignItems: 'start' }}>
        {/* Photo column */}
        <div>
          <div style={{
            width: 110, aspectRatio: '4 / 3',
            background: 'rgba(0,0,0,0.4)', border: `1px solid ${GOLD_HAIRLINE}`,
            borderRadius: 8, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {backdrop.image_url ? (
              <img
                data-testid={`backdrop-thumb-${backdrop.slug}`}
                src={mediaUrl(backdrop.image_url)} alt={backdrop.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: IVORY_GHOST, fontStyle: 'italic', fontSize: 11.5 }}>No photo</span>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*"
            data-testid={`backdrop-photo-input-${backdrop.slug}`}
            onChange={(e) => uploadPhoto(e.target.files?.[0])}
            style={{ display: 'none' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            <button type="button" disabled={uploading}
              data-testid={`backdrop-upload-${backdrop.slug}`}
              onClick={() => fileInputRef.current?.click()}
              style={{ ...ghostPillStyle, fontSize: 11.5, padding: '4px 8px' }}>
              {uploading ? 'Uploading…' : backdrop.image_url ? 'Replace photo' : 'Upload photo'}
            </button>
            {backdrop.image_url && (
              <button type="button" onClick={clearPhoto}
                data-testid={`backdrop-clear-photo-${backdrop.slug}`}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'rgba(184,111,111,0.78)', fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 11, cursor: 'pointer',
                }}>Clear photo</button>
            )}
          </div>
        </div>

        {/* Fields column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input type="text" value={form.name} onChange={upd('name')}
                data-testid={`backdrop-name-${backdrop.slug}`} style={inputBase} />
            </div>
            <div>
              <label style={labelStyle}>Price (£) — blank = On request</label>
              <input type="number" min="0" max="99999" value={form.price} onChange={upd('price')}
                data-testid={`backdrop-price-${backdrop.slug}`} placeholder="e.g. 80" style={inputBase} />
            </div>
            <div>
              <label style={labelStyle}>Display order</label>
              <input type="number" min="0" max="9999" value={form.display_order} onChange={upd('display_order')}
                data-testid={`backdrop-order-${backdrop.slug}`} style={inputBase} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={upd('description')} rows={2}
              data-testid={`backdrop-description-${backdrop.slug}`}
              style={{ ...inputBase, resize: 'vertical', minHeight: 50 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, alignItems: 'end' }}>
            <div>
              <label style={labelStyle}>Accent colour (CSS — top ribbon)</label>
              <input type="text" value={form.accent} onChange={upd('accent')}
                data-testid={`backdrop-accent-${backdrop.slug}`}
                placeholder="rgba(212,170,82,0.5)" style={inputBase} />
            </div>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: SERIF, fontSize: 13, color: IVORY,
              padding: '8px 4px', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={!!form.is_active} onChange={upd('is_active')}
                data-testid={`backdrop-active-${backdrop.slug}`}
                style={{ accentColor: GOLD, width: 16, height: 16 }} />
              Active (visible on /photobooth-hire)
            </label>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4,
          }}>
            <span style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_GHOST, fontSize: 11.5 }}>
              slug: {backdrop.slug}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={remove}
                data-testid={`backdrop-delete-${backdrop.slug}`}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'rgba(184,111,111,0.78)', fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12.5, cursor: 'pointer',
                }}>Hide from customers</button>
              <button type="button" onClick={save} disabled={!dirty || saving}
                data-testid={`backdrop-save-${backdrop.slug}`}
                style={{
                  ...primaryPillStyle,
                  opacity: dirty && !saving ? 1 : 0.4,
                  cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                }}>
                {saving ? 'Saving…' : 'Save changes ✦'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'block', fontFamily: SERIF, fontStyle: 'italic',
  color: IVORY_DIM, fontSize: 11.5, letterSpacing: '0.04em',
  marginBottom: 4,
};

const NewBackdropForm = ({ onCancel, onCreated }) => {
  const [form, setForm] = useState({
    slug: '', name: '', description: '', price: '',
    accent: 'rgba(212,170,82,0.5)', display_order: 100,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    if (!form.slug.trim() || !form.name.trim()) {
      toast.error('Slug and name are required.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/photobooth/backdrops`, {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        description: form.description.trim(),
        price: form.price === '' ? null : Number(form.price),
        accent: form.accent.trim(),
        display_order: Number(form.display_order) || 0,
      });
      toast.success('Backdrop added.');
      await onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create backdrop.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="new-backdrop-form" style={{
      background: 'rgba(212,170,82,0.04)', border: `1px dashed ${GOLD_FAINT}`,
      borderRadius: 10, padding: 18, marginBottom: 16,
    }}>
      <div style={{ fontFamily: SERIF, fontSize: 14, color: IVORY, marginBottom: 12 }}>
        ✦ New backdrop
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>Slug (URL-safe)</label>
          <input type="text" value={form.slug} onChange={upd('slug')}
            placeholder="e.g. neon-graffiti-wall"
            data-testid="new-backdrop-slug" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Name (shown on the card)</label>
          <input type="text" value={form.name} onChange={upd('name')}
            placeholder="e.g. NEON Graffiti Wall"
            data-testid="new-backdrop-name" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Price (£) — blank = On request</label>
          <input type="number" min="0" value={form.price} onChange={upd('price')}
            data-testid="new-backdrop-price" style={inputBase} />
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Description</label>
        <textarea value={form.description} onChange={upd('description')} rows={2}
          data-testid="new-backdrop-description"
          style={{ ...inputBase, resize: 'vertical', minHeight: 50 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginTop: 10 }}>
        <div>
          <label style={labelStyle}>Accent colour (CSS)</label>
          <input type="text" value={form.accent} onChange={upd('accent')}
            data-testid="new-backdrop-accent" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Display order</label>
          <input type="number" min="0" value={form.display_order} onChange={upd('display_order')}
            data-testid="new-backdrop-order" style={inputBase} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <button type="button" onClick={onCancel} style={ghostPillStyle}>Cancel</button>
        <button type="button" onClick={create} disabled={saving}
          data-testid="new-backdrop-create" style={primaryPillStyle}>
          {saving ? 'Adding…' : 'Add backdrop ✦'}
        </button>
      </div>
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_GHOST, fontSize: 11.5, margin: '10px 0 0' }}>
        Photo upload is available after the backdrop is created.
      </p>
    </div>
  );
};

export default AdminBackdropsPage;
